import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { fs, auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { enrollUserInLoyaltyProgram } from '../services/syrveLoyaltySync';
import { normalizePhoneNumber, hasCompletedOnboarding, getUserPhoneNumber } from '../utils/userUtils';
import '../css/UserOnboarding.css';

const UserOnboarding = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [step, setStep] = useState(1);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        personalId: '',
        phoneNumber: '',
        gender: '',
        dateOfBirth: '',
        marketingOptIn: true
    });

    const [formErrors, setFormErrors] = useState({});
    const [checkingDuplicates, setCheckingDuplicates] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser(user);

                try {
                    const userDoc = await getDoc(doc(fs, 'users', user.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        // If onboarding is already completed, pre-fill the form
                        if (hasCompletedOnboarding(userData)) {
                            if (userData.firstName) setFormData(prev => ({...prev, firstName: userData.firstName}));
                            if (userData.lastName) setFormData(prev => ({...prev, lastName: userData.lastName}));
                            if (userData.personalId) setFormData(prev => ({...prev, personalId: userData.personalId}));
                            const canonicalPhone = getUserPhoneNumber(userData);
                            if (canonicalPhone) setFormData(prev => ({...prev, phoneNumber: canonicalPhone}));
                            if (userData.gender) setFormData(prev => ({...prev, gender: userData.gender}));
                            if (userData.dateOfBirth) setFormData(prev => ({...prev, dateOfBirth: userData.dateOfBirth}));
                            if (typeof userData.marketingOptIn === 'boolean') setFormData(prev => ({...prev, marketingOptIn: userData.marketingOptIn}));
                        }
                    }
                } catch (error) {
                    console.error('Error checking user data:', error);
                }
                setLoading(false);
            } else {
                // Not authenticated
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    const checkDuplicates = async (personalId, phoneNumber) => {
        try {
            const usersRef = collection(fs, 'users');

            const personalIdQuery = query(usersRef, where('personalId', '==', personalId));
            const personalIdSnapshot = await getDocs(personalIdQuery);

            if (!personalIdSnapshot.empty) {
                const existingUser = personalIdSnapshot.docs[0].data();
                if (existingUser.uid !== currentUser?.uid) {
                    return {
                        isDuplicate: true,
                        field: 'personalId',
                        message: t('This personal ID is already registered with another account') || 'This personal ID is already registered with another account'
                    };
                }
            }

            const phoneQuery = query(usersRef, where('phoneNumber', '==', phoneNumber));
            const phoneSnapshot = await getDocs(phoneQuery);

            if (!phoneSnapshot.empty) {
                const existingUser = phoneSnapshot.docs[0].data();
                if (existingUser.uid !== currentUser?.uid) {
                    return {
                        isDuplicate: true,
                        field: 'phoneNumber',
                        message: t('This phone number is already registered with another account') || 'This phone number is already registered with another account'
                    };
                }
            }

            return { isDuplicate: false };
        } catch (error) {
            console.error('Error checking duplicates:', error);
            return { isDuplicate: false };
        }
    };

    const validateStep = async (stepNumber) => {
        const errors = {};

        switch(stepNumber) {
            case 1:
                if (!formData.firstName.trim()) {
                    errors.firstName = t('First name is required') || 'First name is required';
                } else if (formData.firstName.length < 2) {
                    errors.firstName = t('First name must be at least 2 characters') || 'First name must be at least 2 characters';
                }

                if (!formData.lastName.trim()) {
                    errors.lastName = t('Last name is required') || 'Last name is required';
                } else if (formData.lastName.length < 2) {
                    errors.lastName = t('Last name must be at least 2 characters') || 'Last name must be at least 2 characters';
                }
                break;

            case 2:
                if (!formData.personalId.trim()) {
                    errors.personalId = t('Personal ID is required') || 'Personal ID is required';
                } else if (!/^\d{11}$/.test(formData.personalId)) {
                    errors.personalId = t('Personal ID must be 11 digits') || 'Personal ID must be 11 digits';
                }

                if (!formData.phoneNumber.trim()) {
                    errors.phoneNumber = t('Phone number is required') || 'Phone number is required';
                } else if (!/^(\+?995)?[0-9]{9}$/.test(formData.phoneNumber.replace(/[\s-]/g, ''))) {
                    errors.phoneNumber = t('Please enter a valid phone number') || 'Please enter a valid phone number';
                }

                if (!formData.gender) {
                    errors.gender = t('Gender is required') || 'Gender is required';
                }

                if (!errors.personalId && !errors.phoneNumber) {
                    setCheckingDuplicates(true);
                    try {
                        const duplicateCheck = await checkDuplicates(formData.personalId, formData.phoneNumber);
                        if (duplicateCheck.isDuplicate) {
                            errors[duplicateCheck.field] = duplicateCheck.message;
                        }
                    } finally {
                        setCheckingDuplicates(false);
                    }
                }
                break;

            case 3:
                if (!formData.dateOfBirth) {
                    errors.dateOfBirth = t('Date of birth is required') || 'Date of birth is required';
                } else {
                    const birthDate = new Date(formData.dateOfBirth);
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();

                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                        age--;
                    }

                    if (age < 13) {
                        errors.dateOfBirth = t('You must be at least 13 years old') || 'You must be at least 13 years old';
                    } else if (age > 120) {
                        errors.dateOfBirth = t('Please enter a valid date of birth') || 'Please enter a valid date of birth';
                    }
                }
                break;

            default:
                break;
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleNextStep = async () => {
        const isValid = await validateStep(step);
        if (isValid) {
            if (step < 3) {
                setStep(step + 1);
            } else {
                handleSubmit();
            }
        }
    };

    const handlePreviousStep = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            localStorage.clear();
            navigate('/');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleSubmit = async () => {
        const isValid = await validateStep(3);
        if (!isValid) return;

        setSubmitting(true);
        setError('');

        // Check if user is authenticated
        if (!currentUser || !currentUser.uid) {
            setError(t('You must be logged in to complete onboarding.') || 'You must be logged in to complete onboarding.');
            setSubmitting(false);
            return;
        }

        try {
            console.log('Attempting to save for user:', currentUser.uid);
            const userDocRef = doc(fs, 'users', currentUser.uid);

            const existingDoc = await getDoc(userDocRef);
            const existingData = existingDoc.exists() ? existingDoc.data() : {};

            const fullName = `${formData.firstName} ${formData.lastName}`;
            const userLogin = existingData.login || currentUser.email?.split('@')[0] || '';
            const normalizedPhone = normalizePhoneNumber(formData.phoneNumber);
            const cardNumber = `${normalizedPhone.replace(/^\+/, '')}${Date.now()}`;

            const userData = {
                ...existingData,
                firstName: formData.firstName,
                lastName: formData.lastName,
                name: fullName,
                personalId: formData.personalId,
                phoneNumber: normalizedPhone,
                cardNumber: cardNumber,
                gender: formData.gender,
                dateOfBirth: formData.dateOfBirth,
                marketingOptIn: formData.marketingOptIn,
                isNewUser: false,
                email: currentUser.email || existingData.email,
                login: userLogin,
                uid: currentUser.uid,
                updatedAt: new Date().toISOString()
            };

            await setDoc(userDocRef, userData, { merge: true });

            // Update localStorage with the complete user data
            localStorage.removeItem('isNewUser');
            localStorage.removeItem('redirectAfterLogin');
            localStorage.setItem('LOGstatus', 'true');
            localStorage.setItem('USER_NAME', fullName);
            localStorage.setItem('USER_LOGIN', userLogin);
            localStorage.setItem('USER_POST', currentUser.email || '');
            localStorage.setItem('USER_ID', currentUser.uid);
            localStorage.setItem('USER_PERSONAL_ID', formData.personalId);
            localStorage.setItem('userProfile', JSON.stringify({
                firstName: formData.firstName,
                lastName: formData.lastName,
                personalId: formData.personalId,
                gender: formData.gender,
                dateOfBirth: formData.dateOfBirth
            }));

            console.log('✅ Onboarding complete, enrolling user in Syrve loyalty...');

            // Enroll user in Syrve loyalty program (non-blocking)
            enrollUserInLoyaltyProgram(currentUser.email, {
                name: fullName,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: normalizedPhone,
                phoneNumber: normalizedPhone,
                cardNumber: cardNumber,
                personalId: formData.personalId,
                gender: formData.gender,
                dateOfBirth: formData.dateOfBirth,
                marketingOptIn: formData.marketingOptIn,
                uid: currentUser.uid
            }).then(result => {
                if (result.success) {
                    console.log('✅ User enrolled in Syrve loyalty program');
                } else {
                    console.error('❌ Failed to enroll in Syrve loyalty:', result.error);
                }
            }).catch(error => {
                console.error('❌ Error enrolling in Syrve loyalty:', error);
            });

            navigate('/');
        } catch (err) {
            console.error('Error saving user data:', err);
            console.error('Error details:', err.message, err.code);
            // Show more specific error message
            let errorMessage = t('Failed to save your information. Please try again.') || 'Failed to save your information. Please try again.';
            if (err.code === 'permission-denied') {
                errorMessage = t('Permission denied. Please make sure you are logged in.') || 'Permission denied. Please make sure you are logged in.';
            } else if (err.message) {
                errorMessage = `Error: ${err.message}`;
            }
            setError(t(errorMessage) || errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="onboarding-container">
                <div className="onboarding-loading">
                    <div className="spinner"></div>
                    <p>{t('Loading...') || 'Loading...'}</p>
                </div>
            </div>
        );
    }

    // If not authenticated, show login message
    if (!currentUser) {
        return (
            <div className="onboarding-container">
                <div className="onboarding-card">
                    <div className="onboarding-header">
                        <h1>{t('Login Required') || 'Login Required'}</h1>
                        <p className="onboarding-subtitle">
                            {t('Please log in to complete your profile') || 'Please log in to complete your profile'}
                        </p>
                    </div>
                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate('/userpage')}
                            className="btn-primary"
                        >
                            {t('Go to Login') || 'Go to Login'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="onboarding-container">
            <div className="onboarding-card">
                <div className="onboarding-header">
                    <h1>{t('Complete Your Profile') || 'Complete Your Profile'}</h1>
                    <p className="onboarding-subtitle">
                        {t('Please provide the following information to continue using the platform') || 'Please provide the following information to continue using the platform'}
                    </p>
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="btn-logout"
                    >
                        {t('Logout') || 'Logout'}
                    </button>
                </div>

                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${(step / 3) * 100}%` }}></div>
                </div>

                <div className="step-indicator">
                    <span className={`step ${step >= 1 ? 'active' : ''}`}>1</span>
                    <span className={`step ${step >= 2 ? 'active' : ''}`}>2</span>
                    <span className={`step ${step >= 3 ? 'active' : ''}`}>3</span>
                </div>

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                <div className="onboarding-form">
                    {step === 1 && (
                        <div className="form-step">
                            <h2>{t('Personal Information') || 'Personal Information'}</h2>

                            <div className="form-group">
                                <label htmlFor="firstName">
                                    {t('First Name') || 'First Name'} *
                                </label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    className={formErrors.firstName ? 'error' : ''}
                                    placeholder={t('Enter your first name') || 'Enter your first name'}
                                    maxLength="50"
                                />
                                {formErrors.firstName && (
                                    <span className="field-error">{formErrors.firstName}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="lastName">
                                    {t('Last Name') || 'Last Name'} *
                                </label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    className={formErrors.lastName ? 'error' : ''}
                                    placeholder={t('Enter your last name') || 'Enter your last name'}
                                    maxLength="50"
                                />
                                {formErrors.lastName && (
                                    <span className="field-error">{formErrors.lastName}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="form-step">
                            <h2>{t('Identification') || 'Identification'}</h2>

                            <div className="form-group">
                                <label htmlFor="personalId">
                                    {t('Personal ID Number') || 'Personal ID Number'} *
                                </label>
                                <input
                                    type="text"
                                    id="personalId"
                                    name="personalId"
                                    value={formData.personalId}
                                    onChange={handleInputChange}
                                    className={formErrors.personalId ? 'error' : ''}
                                    placeholder={t('11 digit ID number') || '11 digit ID number'}
                                    maxLength="11"
                                    pattern="\d{11}"
                                />
                                {formErrors.personalId && (
                                    <span className="field-error">{formErrors.personalId}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="phoneNumber">
                                    {t('Phone Number') || 'Phone Number'} *
                                </label>
                                <input
                                    type="tel"
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleInputChange}
                                    className={formErrors.phoneNumber ? 'error' : ''}
                                    placeholder={t('+995 5XX XXX XXX') || '+995 5XX XXX XXX'}
                                    maxLength="20"
                                />
                                {formErrors.phoneNumber && (
                                    <span className="field-error">{formErrors.phoneNumber}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="gender">
                                    {t('Gender') || 'Gender'} *
                                </label>
                                <select
                                    id="gender"
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleInputChange}
                                    className={formErrors.gender ? 'error' : ''}
                                >
                                    <option value="">{t('Select gender') || 'Select gender'}</option>
                                    <option value="male">{t('Male') || 'Male'}</option>
                                    <option value="female">{t('Female') || 'Female'}</option>
                                    <option value="other">{t('Other') || 'Other'}</option>
                                </select>
                                {formErrors.gender && (
                                    <span className="field-error">{formErrors.gender}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="form-step">
                            <h2>{t('Date of Birth') || 'Date of Birth'}</h2>

                            <div className="form-group">
                                <label htmlFor="dateOfBirth">
                                    {t('Date of Birth') || 'Date of Birth'} *
                                </label>
                                <input
                                    type="date"
                                    id="dateOfBirth"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth}
                                    onChange={handleInputChange}
                                    className={formErrors.dateOfBirth ? 'error' : ''}
                                    max={new Date().toISOString().split('T')[0]}
                                    min="1900-01-01"
                                />
                                {formErrors.dateOfBirth && (
                                    <span className="field-error">{formErrors.dateOfBirth}</span>
                                )}
                            </div>

                            <div className="form-group checkbox-group">
                                <label htmlFor="marketingOptIn" className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        id="marketingOptIn"
                                        name="marketingOptIn"
                                        checked={formData.marketingOptIn}
                                        onChange={handleInputChange}
                                    />
                                    <span>{t('I agree to receive marketing notifications and special offers') || 'I agree to receive marketing notifications and special offers'}</span>
                                </label>
                            </div>

                            <div className="form-summary">
                                <h3>{t('Review Your Information') || 'Review Your Information'}</h3>
                                <div className="summary-item">
                                    <span className="summary-label">{t('Name') || 'Name'}:</span>
                                    <span className="summary-value">{formData.firstName} {formData.lastName}</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">{t('Personal ID') || 'Personal ID'}:</span>
                                    <span className="summary-value">{formData.personalId}</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">{t('Phone Number') || 'Phone Number'}:</span>
                                    <span className="summary-value">{formData.phoneNumber}</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">{t('Gender') || 'Gender'}:</span>
                                    <span className="summary-value">
                                        {formData.gender ? t(formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1)) || formData.gender : ''}
                                    </span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">{t('Date of Birth') || 'Date of Birth'}:</span>
                                    <span className="summary-value">{formData.dateOfBirth}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="form-actions">
                        {step > 1 && (
                            <button
                                type="button"
                                onClick={handlePreviousStep}
                                className="btn-secondary"
                                disabled={submitting}
                            >
                                {t('Previous') || 'Previous'}
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={handleNextStep}
                            className="btn-primary"
                            disabled={submitting || checkingDuplicates}
                        >
                            {submitting ? (
                                <span>{t('Saving...') || 'Saving...'}</span>
                            ) : checkingDuplicates ? (
                                <span>{t('Checking...') || 'Checking...'}</span>
                            ) : step === 3 ? (
                                <span>{t('Complete Profile') || 'Complete Profile'}</span>
                            ) : (
                                <span>{t('Next') || 'Next'}</span>
                            )}
                        </button>
                    </div>
                </div>

                <div className="onboarding-footer">
                    <p className="privacy-note">
                        {t('Your information is securely stored and will not be shared without your consent.') ||
                         'Your information is securely stored and will not be shared without your consent.'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default UserOnboarding;
